import { Box, Button, Center, Container, Flex, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import moment from "moment";
import { useState, useMemo, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import AppBreadcrumb from "components/AppBreadcrumb";
import { report_api } from "./report.service";
import { showErrorToast, showSuccessToast } from "utilities/Toast";
import { IconDownload, IconPrinter, IconEye } from "@tabler/icons-react";
import { Modal } from "@mantine/core";

export default function ItemWiseSummaryReport() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  const [opened, setOpened] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [grandTotal, setGrandTotal] = useState(0);
  const [totalDC, setTotalDC] = useState(0);
  const [totalPC, setTotalPC] = useState(0);
  const [totaldiscount, setTotaldiscout] = useState(0);

  const handleOpenUpdate = (rowData) => {
    const sameInvoiceProducts = tableData.filter(
      (item) => item.invoice_no === rowData.invoice_no
    );

    if (sameInvoiceProducts.length === 0) return;

    const invoiceSummary = {
      invoice_no: rowData.invoice_no,
      coupon_amount: sameInvoiceProducts.reduce(
        (sum, item) => sum + (Number(item.coupon_amount) || 0),
        0
      ),
      delivery_charge: sameInvoiceProducts.reduce(
        (sum, item) => sum + (Number(item.delivery_charge) || 0),
        0
      ),
      packing_charge: sameInvoiceProducts.reduce(
        (sum, item) => sum + (Number(item.packing_charge) || 0),
        0
      ),
      discount_total: sameInvoiceProducts.reduce(
        (sum, item) => sum + (Number(item.discount_total) || 0),
        0
      ),
      total: sameInvoiceProducts.reduce(
        (sum, item) => sum + (Number(item.invoice_total) || 0),
        0
      ),
    };

    setSelectedRow(invoiceSummary);   // 👈 now object, not array
    setOpened(true);
  };





  // Function to check if a unit allows decimals (like KGS, GM, etc.)
  const isDecimalUnit = (unit) => {
    if (!unit) return false;
    const lowerUnit = unit.toLowerCase();
    const decimalUnits = ['kgs', 'gm', 'kg', 'gram', 'ltr', 'ml', 'mtr'];
    return decimalUnits.includes(lowerUnit);
  };

  // Calculate total Nos and Kgs from table data
  const getTotalQuantityBreakdown = () => {
    if (!Array.isArray(tableData)) {
      return { totalNos: 0, totalKgs: 0 };
    }

    let totalNos = 0; // Whole numbers (PCS/NOS)
    let totalKgs = 0; // Decimal numbers (KGS/GRAM)

    tableData.forEach((item) => {
      const qty = parseFloat(item.qty) || 0;
      const unit = item.units || ''; // Assuming 'units' field exists in your response

      if (isDecimalUnit(unit)) {
        // For decimal units, add to kgs
        totalKgs += qty;
      } else {
        // For whole number units, add to nos
        totalNos += qty;
      }
    });

    return { totalNos, totalKgs };
  };

  // Get quantity breakdown for filtered rows
  const getFilteredQuantityBreakdown = (filteredRows) => {
    if (!Array.isArray(filteredRows)) {
      return { nos: 0, kgs: 0 };
    }

    let nos = 0;
    let kgs = 0;

    filteredRows.forEach((row) => {
      const item = row.original;
      const qty = parseFloat(item.qty) || 0;
      const unit = item.units || '';

      if (isDecimalUnit(unit)) {
        kgs += qty;
      } else {
        nos += qty;
      }
    });

    return { nos, kgs };
  };

  // Move useReactToPrint to top level
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Itemwise_Summary_${moment().format("DDMMYYYY_HHmm")}`,
    onAfterPrint: () =>
      showSuccessToast({
        title: "Success",
        message: "Printed successfully",
      }),
  });

  const getGSTSummary = (rows) => {
    const gstMap = {};

    rows.forEach((row) => {
      const item = row.original;
      const gstPercent = Number(item.tax) || 0;
      const qty = Number(item.qty) || 0;
      const taxAmount = Number(item.tax_amount) || 0;
      const amount = Number(item.amount) || 0;
      const taxable = Number(item.taxable) || 0;

      if (!gstMap[gstPercent]) {
        gstMap[gstPercent] = {
          gstPercent: `${gstPercent}%`,
          rowCount: 0,
          salesQty: 0,
          taxAmount: 0,
          amount: 0,
          taxable: 0,
        };
      }

      gstMap[gstPercent].salesQty += qty;
      gstMap[gstPercent].rowCount += 1;
      gstMap[gstPercent].taxAmount += taxAmount;
      gstMap[gstPercent].taxable += taxable;
      gstMap[gstPercent].amount += amount;
    });

    return Object.values(gstMap);
  };

  const fetchReport = async () => {
     if (!startDate) {
    showErrorToast({
      title: "Error",
      message: "Please select start date",
    });
    return;
  }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        start_date: moment(startDate).format("DD/MM/YYYY"),
        end_date: moment(endDate || startDate).format("DD/MM/YYYY"),
      }).toString();

      const response = await report_api.get_itemwise_summary_report(params);

      if (!response.success) {
        showErrorToast({
          title: "Error",
          message: "Something went wrong",
        });
        setTableData([]);
        return;
      }

      setTableData(response.data || []);
      setTotalDC(response.totals?.total_delivery_charge || 0);
      setTotalPC(response.totals?.total_packing_charge || 0);
      setGrandTotal(response.totals?.grand_total || 0);
      setTotaldiscout(response.totals?.total_discount || 0)

    } catch (error) {
      showErrorToast({
        title: "Error",
        message: "Failed to fetch report",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  
  const columns = useMemo(
    () => [
      {
        header: "Invoice No",
        accessorKey: "invoice_no",
        size: 90,
        minSize: 80,
        maxSize: 100,
      },
      {
        header: "Date",
        accessorKey: "date",
        size: 90,
        minSize: 80,
        maxSize: 100,
      },
      {
        header: "Product",
        accessorKey: "product",
        size: 150,
        minSize: 120,
        maxSize: 200,
      },
      {
        header: "Batch",
        accessorKey: "batch",
        size: 60,
        minSize: 50,
        maxSize: 70,
      },
      {
        header: "Qty",
        accessorKey: "qty",
        size: 40,
        minSize: 30,
        maxSize: 50,
        Cell: ({ cell, row }) => {
          const qty = parseFloat(cell.getValue() || 0);
          const unit = row.original.units || "PCS";

          // Show different formatting based on unit type
          if (isDecimalUnit(unit)) {
            // For KGS/GM units, show 3 decimal places
            return qty.toFixed(3);
          } else {
            // For PCS/NOS units, show whole number
            return qty.toFixed(0);
          }
        },
      },
      {
        header: "Rate",
        accessorKey: "rate",
        size: 60,
        minSize: 50,
        maxSize: 70,
        Cell: ({ cell }) => `₹${parseFloat(cell.getValue() || 0).toFixed(2)}`,
      },
      {
        header: "Tax %",
        accessorKey: "tax",
        size: 40,
        minSize: 30,
        maxSize: 50,
        Cell: ({ cell }) => `${cell.getValue() || 0}%`,
      },
      {
        header: "Tax Amt",
        accessorKey: "tax_amount",
        size: 80,
        minSize: 70,
        maxSize: 90,
        Cell: ({ cell }) => `₹${parseFloat(cell.getValue() || 0).toFixed(2)}`,
      },
      {
        header: "Taxable",
        accessorKey: "taxable",
        size: 80,
        minSize: 70,
        maxSize: 90,
        Cell: ({ cell }) => `₹${parseFloat(cell.getValue() || 0).toFixed(2)}`,
      },
      {
        header: "Amount",
        accessorKey: "amount",
        size: 80,
        minSize: 70,
        maxSize: 90,
        Cell: ({ cell }) => `₹${parseFloat(cell.getValue() || 0).toFixed(2)}`,
      },
      {
        header: "View",
        accessorKey: "view",
        size: 60,
        Cell: ({ row }) => {
          const currentInvoice = row.original.invoice_no;

          const invoiceRows = tableData.filter(
            (item) => item.invoice_no === currentInvoice
          );

          const lastInvoiceRow = invoiceRows[invoiceRows.length - 1];

          // Only show icon for last item of that invoice
          if (row.original !== lastInvoiceRow) return null;

          return (
            <IconEye
              size={18}
              style={{ cursor: "pointer", color: "green" }}
              onClick={() => handleOpenUpdate(row.original)}
            />
          );
        },
      }



    ],
    [tableData]
  );

  const invoiceCounts = useMemo(() => {
    const counts = {};
    tableData.forEach((row) => {
      counts[row.invoice_no] = (counts[row.invoice_no] || 0) + 1;
    });
    return counts;
  }, [tableData]);

  const invoiceColorMap = useMemo(() => {
    const map = {};
    let index = 0;

    tableData.forEach((row) => {
      if (invoiceCounts[row.invoice_no] > 1 && !map[row.invoice_no]) {
        map[row.invoice_no] = index++;
      }
    });

    return map;
  }, [tableData, invoiceCounts]);

  const invoiceColors = [
    "#f9e8c5ff", // yellow
    "#aef1dcff", // green
    "#a0cbeaff", // blue
    "#d3caf6ff", // violet
    "#eab9cdff", // pink
  ];

  const table = useMantineReactTable({
    columns,
    data: tableData,
    state: {
      isLoading: loading,
    },
    enableStickyHeader: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    enableRowStriping: false,
    enableHover: false,
    mantineTableContainerProps: { style: {    maxHeight: "350px",   // 👈 4 rows + header + footer space
      overflowY: "auto", } },
    mantineTableBodyCellProps: ({ row }) => {
      const invoiceNo = row.original.invoice_no;

      // Single-row invoice → no color
      if (invoiceCounts[invoiceNo] <= 1) {
        return {};
      }

      const colorIndex = invoiceColorMap[invoiceNo];

      return {
        sx: {
          backgroundColor: `${invoiceColors[colorIndex % invoiceColors.length]} !important`,
        },
      };
    },
  });

  const filteredRows = table.getFilteredRowModel().rows;

const hasFilter =
  table.getState().columnFilters.length > 0 ||
  table.getState().globalFilter;

const filteredSummaryTotals = {
  delivery_charge: filteredRows.reduce(
    (sum, row) => sum + (Number(row.original.delivery_charge) || 0),
    0
  ),
  packing_charge: filteredRows.reduce(
    (sum, row) => sum + (Number(row.original.packing_charge) || 0),
    0
  ),

  coupon_total: filteredRows.reduce(
    (sum, row) => sum + (Number(row.original.coupon_amount) || 0),
    0
  ),

  discount_total: filteredRows.reduce(
    (sum, row) => sum + (Number(row.original.discount_total) || 0),
    0
  ),

  grand_total: filteredRows.reduce(
    (sum, row) => sum + (Number(row.original.invoice_total) || 0),
    0
  ),
};



  const filteredTotals = {
    qty: filteredRows.reduce((sum, row) => sum + (row.original.qty || 0), 0),
    amount: filteredRows.reduce((sum, row) => sum + (row.original.amount || 0), 0),
    tax_amount: filteredRows.reduce((sum, row) => sum + (row.original.tax_amount || 0), 0),
    taxable: filteredRows.reduce((sum, row) => sum + (row.original.taxable || 0), 0),
  };

  // Get quantity breakdown for filtered rows
  const { nos: filteredNos, kgs: filteredKgs } = getFilteredQuantityBreakdown(filteredRows);

  // Download CSV function
  const downloadCSV = () => {
    const filteredRows = table.getFilteredRowModel().rows;
    


    if (filteredRows.length === 0) {
      showErrorToast({
        title: "Error",
        message: "No data to export",
      });
      return;
    }

    const summary = getGSTSummary(filteredRows);
    const totalSales = summary.reduce((a, b) => a + b.rowCount, 0);
    const totalTax = summary.reduce((a, b) => a + b.taxAmount, 0);
    const totalAmount = summary.reduce((a, b) => a + b.amount, 0);
    const totalTaxable = summary.reduce((a, b) => a + b.taxable, 0);

    // CSV rows (ONLY 3 columns)
    const csvRows = [
      ["Itemwise Summary Report"],
      [`Start Date: ${moment(startDate).format("DD/MM/YYYY")}`],
      [`End Date: ${moment(endDate).format("DD/MM/YYYY")}`],
      [], // blank line
      ["Taxable (GST%)", "Sales","Taxable", "Tax Amount", "Amount"], // header
      ...summary.map((s) => [
        s.gstPercent,
        s.rowCount,
         s.taxable.toFixed(2),
        s.taxAmount.toFixed(2),
        s.amount.toFixed(2),
      ]),
      [],
      ["TOTAL", totalSales, totalTaxable.toFixed(2), totalTax.toFixed(2), totalAmount.toFixed(2)],
      [],  
  ["Summary Totals"],

  [
    "Total DC",
    "",
    "",
    "",
    hasFilter
      ? filteredSummaryTotals.delivery_charge.toFixed(2)
      : Number(totalDC).toFixed(2),
  ],
  [
    "Total PC",
    "",
    "",
    "",
    hasFilter
      ? filteredSummaryTotals.packing_charge.toFixed(2)
      : Number(totalPC).toFixed(2),
  ],
  [
    "Total Discount",
    "",
    "",
    "",
`-${(
    hasFilter
      ? filteredSummaryTotals.coupon_total +
        filteredSummaryTotals.discount_total
      : Number(totaldiscount)
  ).toFixed(2)}`
  ],
  [
    "Grand Total",
    "",
    "",
    "",
    hasFilter
      ? filteredSummaryTotals.grand_total.toFixed(2)
      : Number(grandTotal).toFixed(2),
  ],
    ];


    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map((e) => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.href = encodedUri;

    link.download = `ItemwiseSummary_${moment(startDate).format(
      "DD-MM-YYYY"
    )}_to_${moment(endDate).format("DD-MM-YYYY")}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccessToast({
      title: "Success",
      message: "CSV Successfully Downloaded",
    });
  };

  // Print Component
  const PrintComponent = () => {
    const filteredRows = table.getFilteredRowModel().rows;
    const summary = getGSTSummary(filteredRows);
    const totalSales = summary.reduce((s, x) => s + x.rowCount, 0);
    const totalTax = summary.reduce((s, x) => s + x.taxAmount, 0);
    const totalAmount = summary.reduce((s, x) => s + x.amount, 0);
    const totalTaxable = summary.reduce((s, x) => s + x.taxable, 0);

    if (summary.length === 0) {
      return null;
    }

    return (
      <div ref={printRef} style={{ padding: "20px" }}>
        <h1 style={{ textAlign: "center", marginBottom: "5px" }}>
          Itemwise Summary Report
        </h1>

        {startDate && endDate && (
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <p>
              <strong>Period:</strong> {moment(startDate).format("DD/MM/YYYY")} to{" "}
              {moment(endDate).format("DD/MM/YYYY")}
            </p>
          </div>
        )}

        <table
          border="1"
          cellPadding="8"
          cellSpacing="0"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th>Taxable (GST%)</th>
              <th>Sales </th>
              <th>Taxable</th>
              <th>Tax Amount</th>
              <th>Amount</th>
            </tr>
          </thead>

          <tbody>
            {summary.map((s, index) => (
              <tr key={index}>
                <td style={{ textAlign: "center" }}>{s.gstPercent}</td>
                <td style={{ textAlign: "center" }}>{s.rowCount}</td>
                <td style={{ textAlign: "right" }}>{s.taxable.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{s.taxAmount.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{s.amount.toFixed(2)}</td>
              </tr>
            ))}

            <tr style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}>
              <td style={{ textAlign: "center" }}>TOTAL</td>
              <td style={{ textAlign: "center" }}>{totalSales}</td>
              <td style={{ textAlign: "right" }}>{totalTaxable.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{totalTax.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{totalAmount.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ marginTop: "30px" }}>
  <h3>Summary Totals</h3>

  <table
    border="1"
    cellPadding="8"
    cellSpacing="0"
    style={{ width: "50%", borderCollapse: "collapse" }}
  >
    <tbody>
      <tr>
        <td>Total DC</td>
        <td style={{ textAlign: "right" }}>
          ₹
          {hasFilter
            ? filteredSummaryTotals.delivery_charge.toFixed(2)
            : Number(totalDC).toFixed(2)}
        </td>
      </tr>

      <tr>
        <td>Total PC</td>
        <td style={{ textAlign: "right" }}>
          ₹
          {hasFilter
            ? filteredSummaryTotals.packing_charge.toFixed(2)
            : Number(totalPC).toFixed(2)}
        </td>
      </tr>

      <tr>
        <td>Total Discount</td>
        <td style={{ textAlign: "right", color: "red" }}>
          -₹
          {hasFilter
            ? (
                filteredSummaryTotals.coupon_total +
                filteredSummaryTotals.discount_total
              ).toFixed(2)
            : Number(totaldiscount).toFixed(2)}
        </td>
      </tr>

      <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
        <td>Grand Total</td>
        <td style={{ textAlign: "right" }}>
          ₹
          {hasFilter
            ? filteredSummaryTotals.grand_total.toFixed(2)
            : Number(grandTotal).toFixed(2)}
        </td>
      </tr>
    </tbody>
  </table>
</div>


        <div style={{ textAlign: "center", marginTop: "15px", fontSize: "12px" }}>
          Generated by QuZone Counter System
        </div>
      </div>
    );
  };

  return (
    <Container fluid>
      {/* Hidden Print Component */}
      <div style={{ display: "none" }}>
        <PrintComponent />
      </div>

      <Box p={25}>
        <Flex justify="space-between" align="end" wrap="wrap">
          <Box>
            <Text size="lg" fw={600} mb={10}>
              Itemwise Summary Report
            </Text>
            <AppBreadcrumb />
          </Box>

          <Flex gap={20} wrap="wrap" align="end" style={{ marginRight: "100px" }}>
            <Button
              onClick={downloadCSV}
              leftIcon={<IconDownload />}
            >
              CSV
            </Button>
            <Button
              leftIcon={<IconPrinter />}
              onClick={() => {
                if (tableData.length === 0) {
                  showErrorToast({
                    title: "Error",
                    message: "No data to print",
                  });
                  return;
                }
                handlePrint();
              }}
            >
              Print
            </Button>
            <DatePickerInput
              label="Start Date"
              placeholder="Select Date"
              value={startDate}
              onChange={setStartDate}
              maw={300}
            />

            <DatePickerInput
              label="End Date"
              placeholder="Select Date"
              value={endDate}
              onChange={setEndDate}
              disabled={!startDate}
              maw={300}
            />

            <Button color="green" onClick={fetchReport}>
              Search
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Box px={25}>
        {/* Scrollable table area */}
<Box px={25}>
  <MantineReactTable table={table} />
</Box>

        {/* Totals footer Section */}
        <Box
          mt={10}
          p={15}
          bg="#f8f9fa"
          style={{
            borderTop: "1px solid #ddd",
            width: "100%",
            position: "fixed",
            bottom: 0,
            left: 0,
            zIndex: 20,
            boxShadow: "0 -1px 5px rgba(0,0,0,0.1)",
          }}
        >
          <Flex gap={25} mt={10} align="center" justify="center">
            <Text
              sx={{
                fontWeight: 900,
                fontSize: 24,
                textAlign: "center",
              }}
            >
              Summary Totals
            </Text>

            {/* Display Nos and Kgs separately */}
            <Text fw={800}>
              Total Qty :
              {filteredNos > 0 ? ` ${filteredNos.toFixed(0)} Nos` : ''}
              {filteredNos > 0 && filteredKgs > 0 ? ' / ' : ''}
              {filteredKgs > 0 ? ` ${filteredKgs.toFixed(3)} Kgs` : ''}
              {filteredNos === 0 && filteredKgs === 0 ? '0' : ''}
            </Text>
<Text fw={800}>
  Total DC: ₹
  {hasFilter
    ? filteredSummaryTotals.delivery_charge.toFixed(2)
    : Number(totalDC).toFixed(2)}
</Text>

<Text fw={800}>
  Total PC: ₹
  {hasFilter
    ? filteredSummaryTotals.packing_charge.toFixed(2)
    : Number(totalPC).toFixed(2)}
</Text>

<Text fw={800}>
  Total Dis:{" "}
  <span style={{ color: "red" }}>
    -₹
    {hasFilter
      ? (
          filteredSummaryTotals.coupon_total +
          filteredSummaryTotals.discount_total
        ).toFixed(2)
      : Number(totaldiscount).toFixed(2)}
  </span>
</Text>

<Text fw={800}>
  Total Taxable: ₹{filteredTotals.taxable.toFixed(2)}
</Text>

<Text fw={800}>
  Total Tax: ₹{filteredTotals.tax_amount.toFixed(2)}
</Text>

<Text fw={800}>
  Total Amount: ₹
  {hasFilter
    ? filteredSummaryTotals.grand_total.toFixed(2)
    : Number(grandTotal).toFixed(2)}
</Text>

          </Flex>
        </Box>
      </Box>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Invoice Products"
        centered
        size="xl"
      >
        {selectedRow && (
          <table
            border="1"
            cellPadding="8"
            style={{ width: "100%", borderCollapse: "collapse" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <th>Invoice No</th>
                <th>Coupon Amount</th>
                <th>Discount Amount</th>
                <th>Delivery Charge</th>
                <th>Packing Charge</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{selectedRow.invoice_no}</td>
                <td>- ₹{selectedRow.coupon_amount}</td>
                <td>- ₹{selectedRow.discount_total}</td>
                <td>₹{selectedRow.delivery_charge}</td>
                <td>₹{selectedRow.packing_charge}</td>
                <td>₹{selectedRow.total}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Modal>



    </Container>
  );
}